import { Alert, App, Button, Card, Input, Space, Spin, Tabs } from 'antd';
import {
  KeyOutlined,
  SafetyCertificateOutlined,
  MailOutlined,
  MessageOutlined,
  IdcardOutlined,
  MobileOutlined,
  NotificationOutlined,
  SendOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { get, post } from '../../api/client';
import { NamespaceConfigForm } from './components/NamespaceConfigForm';
import type { NamespaceConfig } from './components/NamespaceConfigForm';

interface ProviderTab {
  key: string;
  label: string;
  onlyKeys: string[];
  guide: string;
}

function useConfig(name: string) {
  return useQuery({
    queryKey: ['system-config', name],
    queryFn: async () => (await get<unknown>(`/admin/system-config/${name}`)) as NamespaceConfig,
  });
}

function CredentialSection({
  name,
  onlyKeys,
  desc,
}: {
  name: string;
  onlyKeys: string[];
  desc: string;
}) {
  const { data, isLoading } = useConfig(name);
  if (isLoading || !data) return <Spin />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Alert type="info" showIcon message={desc} />
      <NamespaceConfigForm config={data} onlyKeys={onlyKeys} ignoreShowWhen />
    </div>
  );
}

function SmtpCredentialPanel() {
  const { data, isLoading } = useConfig('smtp');
  const { message } = App.useApp();
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  if (isLoading || !data) return <Spin />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Alert
        type="info"
        showIcon
        message="邮件 SMTP 全部连接参数集中在此维护"
        description="系统邮件（注册邮箱验证码、密码重置、客户档案通知）与「留言通知 → 邮件」转发共用这套 SMTP。发件密码/授权码在邮箱服务商后台生成、不是邮箱登录密码，加密存储、回显脱敏。填好保存后可用右上角「发送测试邮件」验证链路。"
      />
      <NamespaceConfigForm
        config={data}
        cardTitle="邮件 SMTP"
        extraActions={
          <>
            <Input
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="测试邮箱"
              style={{ width: 180 }}
              type="email"
            />
            <Button
              icon={<SendOutlined />}
              loading={testing}
              onClick={async () => {
                if (!testEmail) {
                  message.warning('请填写测试邮箱地址');
                  return;
                }
                setTesting(true);
                try {
                  const resp = await post<{ success: boolean; message: string }>('/admin/system-config/smtp/test', {
                    email: testEmail,
                  });
                  if (resp.success) message.success(resp.message);
                  else message.error(resp.message);
                } catch (error) {
                  message.error(error instanceof Error ? error.message : '测试失败');
                } finally {
                  setTesting(false);
                }
              }}
            >
              发送测试邮件
            </Button>
          </>
        }
      />
    </div>
  );
}

function NotifyEventSwitch() {
  const { data, isLoading } = useConfig('notify');
  if (isLoading || !data) return <Spin />;
  return (
    <NamespaceConfigForm
      config={data}
      onlyKeys={['applicationEnabled', 'transferEnabled']}
      switchCardKeys={['applicationEnabled', 'transferEnabled']}
      ignoreShowWhen
      cardTitle="通知事件"
      emptyHint="暂无可勾选的通知事件"
    />
  );
}

function ProviderPanel({
  config,
  provider,
}: {
  config: NamespaceConfig;
  provider: ProviderTab;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Alert type="info" showIcon message="配置方法" description={provider.guide} />
      <NamespaceConfigForm
        config={config}
        onlyKeys={provider.onlyKeys}
        ignoreShowWhen
        cardTitle={`${provider.label} 凭据`}
      />
    </div>
  );
}

function ProviderTabsSection({
  intro,
  configName,
  providers,
}: {
  intro: string;
  configName: string;
  providers: ProviderTab[];
}) {
  const { data, isLoading } = useConfig(configName);
  if (isLoading || !data) return <Spin />;
  // 只有一个运营商时不再套一层空 tab，直接平铺；多运营商才分 tab。
  if (providers.length === 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Alert type="info" showIcon message={intro} />
        <ProviderPanel config={data} provider={providers[0]} />
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Alert type="info" showIcon message={intro} />
      <Tabs
        type="card"
        items={providers.map((p) => ({
          key: p.key,
          label: p.label,
          children: <ProviderPanel config={data} provider={p} />,
        }))}
      />
    </div>
  );
}

const SMS_PROVIDERS: ProviderTab[] = [
  {
    key: 'aliyun',
    label: '阿里云',
    onlyKeys: ['aliyunAccessKeyId', 'aliyunAccessKeySecret'],
    guide:
      '1. 阿里云控制台 → RAM 访问控制 → 用户 → 创建 AccessKey，获取 AccessKeyId 与 AccessKeySecret；2. 短信服务 → 国内消息 → 签名管理（审核通过）→ 模板管理（验证码模板需含 ${code} 变量）。签名与模板在注册流程页「手机号持有验证」步配置。',
  },
  // 腾讯云短信没有标签页：后端 sms.service 只有 `// TODO: 实际调用腾讯云 SMS SDK`，
  // 选中它等于验证码短信一条都发不出去，故连 provider 选项带密钥字段一并从 sms 组移除。
  // 实名认证同理（服务层那段 tencent 是把身份证号拼进 URL 的伪实现），系统真实渠道只有阿里云三要素与新诺维尔。
];

const IDENTITY_PROVIDERS: ProviderTab[] = [
  {
    key: 'aliyun',
    label: '阿里云',
    onlyKeys: ['accessKeyId', 'accessKeySecret', 'apiEndpoint'],
    guide:
      '1. 阿里云控制台 → RAM → 创建 AccessKey；2. 开通「号码认证 / 实人认证」服务（三要素核验）；3. 填入 AccessKeyId / AccessKeySecret。地域与超时在注册流程页「实名核验」步配置。',
  },
  {
    key: 'newnorwell',
    label: 'newnorwell',
    onlyKeys: ['appId', 'appKey'],
    guide: '1. 联系 newnorwell 服务商开通三要素核验服务；2. 获取 AppId 与 AppKey；3. 填入下方。',
  },
];

const NOTIFY_CHANNELS: ProviderTab[] = [
  {
    key: 'wecom',
    label: '企业微信',
    onlyKeys: ['wecomEnabled', 'wecomWebhook'],
    guide:
      '1. 在企业微信群里点群名 → 聊天信息 → 消息推送 → 添加，把机器人命名为「实时通知」；2. 添加成功后复制 Webhook 地址（https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=…）填入下方；3. 打开上方「启用企业微信通知」开关保存即生效。Webhook 地址本身就是凭据，加密存储、保存后回显为脱敏值。留言、待审申请与转账待核对共用这一个群，按需在上方「通知事件」勾选要推哪些。',
  },
  {
    key: 'dingtalk',
    label: '钉钉',
    onlyKeys: ['dingtalkEnabled', 'dingtalkWebhook', 'dingtalkSecret'],
    guide:
      '1. 钉钉群 → 群设置 → 智能群助手 → 添加机器人 → 自定义；2. 安全设置任选其一：勾选「加签」则复制 SEC 开头的密钥填入「加签密钥」；若选「自定义关键词」，因留言与待审申请标题不同，请把关键词设为两者都含的词（如「通知」）；3. 复制 Webhook 地址（https://oapi.dingtalk.com/robot/send?access_token=…）填入；4. 打开「启用钉钉通知」开关保存。',
  },
  {
    key: 'feishu',
    label: '飞书',
    onlyKeys: ['feishuEnabled', 'feishuWebhook', 'feishuSecret'],
    guide:
      '1. 飞书群 → 设置 → 群机器人 → 添加机器人 → 自定义机器人；2. 安全设置若勾选「签名校验」，复制生成的密钥填入「签名校验密钥」，未开启则留空；3. 复制 Webhook 地址（https://open.feishu.cn/open-apis/bot/v2/hook/…）填入；4. 打开「启用飞书通知」开关保存。',
  },
  {
    key: 'email',
    label: '邮件',
    onlyKeys: ['emailEnabled', 'emailTo'],
    guide:
      '被勾选的事件（留言 / 待审申请）通过系统 SMTP（全部连接参数含发件账号、密码/授权码在「邮件 SMTP」标签页维护）转发到指定运营邮箱。1. 填写接收邮箱（多个用英文逗号分隔）；2. 打开「启用邮件转发通知」开关保存。只开开关不填邮箱则邮件通道不发送。',
  },
];

export default function CredentialsPage() {
  const navigate = useNavigate();
  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              <KeyOutlined style={{ color: '#0d9488', marginRight: 8 }} />
              第三方服务凭据
            </div>
            <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
              集中维护各服务商密钥（加密存储）。六类凭据按上方标签页分开维护（邮件 / 短信 / 实名认证 / 推送 / 地图 / 实时通知），同一类里若有多个运营商或通道再按小标签切换。邮件 SMTP 的全部连接参数已收进「邮件 SMTP」标签页一处配好；短信、实名认证等只需在此填密钥，业务开关、签名、模板在注册流程页按步骤配置。
            </div>
          </div>
          <Space>
            <Button icon={<SafetyCertificateOutlined />} onClick={() => navigate('/settings/register-flow')}>注册流程</Button>
            <Button onClick={() => navigate('/settings')}>返回</Button>
          </Space>
        </div>
      </Card>

      <Card styles={{ body: { paddingTop: 8 } }}>
        <Tabs
          items={[
            {
              key: 'smtp',
              label: (
                <span>
                  <MailOutlined /> 邮件 SMTP
                </span>
              ),
              children: <SmtpCredentialPanel />,
            },
            {
              key: 'sms',
              label: (
                <span>
                  <MessageOutlined /> 短信服务
                </span>
              ),
              children: (
                <ProviderTabsSection
                  configName="sms"
                  intro="配置阿里云短信 AccessKey / Secret。配好后到注册流程页「手机号持有验证」步选择该服务商即可发送短信。"
                  providers={SMS_PROVIDERS}
                />
              ),
            },
            {
              key: 'identity',
              label: (
                <span>
                  <IdcardOutlined /> 实名认证
                </span>
              ),
              children: (
                <ProviderTabsSection
                  configName="identity"
                  intro="按运营商分别配置核验密钥。配好后到注册流程页「实名核验」步选择对应服务商并开启实名认证即可生效。"
                  providers={IDENTITY_PROVIDERS}
                />
              ),
            },
            {
              key: 'push',
              label: (
                <span>
                  <MobileOutlined /> 推送服务
                </span>
              ),
              children: (
                <CredentialSection
                  name="push"
                  onlyKeys={['appId', 'appSecret']}
                  desc="华为推送服务（Push Kit）凭证：app 关闭/退后台时通过华为推送通道投递订单通知。在 AppGallery Connect 控制台开通推送服务后，从「项目设置 → 常规 → 应用信息」获取 AppId 与 Client Secret 填入下方。未配置时推送静默降级，不影响业务。"
                />
              ),
            },
            {
              key: 'amap',
              label: (
                <span>
                  <EnvironmentOutlined /> 地图服务
                </span>
              ),
              children: (
                <CredentialSection
                  name="amap"
                  onlyKeys={['webKey']}
                  desc="高德地图 Web 服务 Key：订单活动地址点「导航」时，服务端先把地址文本解析成经纬度，地图 App 的终点才能自动落位（高德路线规划只认经纬度，光有地址终点栏是空的）。在高德开放平台 lbs.amap.com → 控制台 → 应用管理 → 创建应用（平台类型选「Web 服务」）→ 添加 Key 后填入下方。个人认证 15 万次/月免费，超出 30 元/万次、不会静默扣费；对外商用需另购技术服务许可。未配置时各端导航退化为「按地址关键词检索」，功能不受影响。"
                />
              ),
            },
            {
              key: 'notify',
              label: (
                <span>
                  <NotificationOutlined /> 实时通知
                </span>
              ),
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <NotifyEventSwitch />
                  <ProviderTabsSection
                    configName="notify"
                    intro="运营侧实时推送：官网「联系我们」新留言、新的人工审核待审申请、会员银行转账待核对。先在上「通知事件」勾选要推哪些，再在下方各通道填 Webhook / 邮箱并开启——被勾选事件会共用同一套已启用通道。四条通道相互独立、可多选并用；全部关闭时业务照常入库，只是不额外推送。"
                    providers={NOTIFY_CHANNELS}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
